import * as Logger from './logger.js';
import { Auth } from './auth.js';
import { HSComponent } from './component.js';
import { UserSession } from './session.js';
import { hasCapability } from './capabilities.js';
import SlickGridUtils from './slick-grid-utils.js';
import { buildRankedTokens, getSuggestedTokens, itemMatchesActiveTokens, itemMatchesQuery } from './token-search.js';
import { convertJsonToCSV } from './utils.js';
import styles from './log-viewer.css';
import template from './log-viewer.html';

const MAX_LOGS_IN_MEMORY = 5000;
const MEMORY_WARNING_MB = 50;
const PREFETCH_LIMIT = 1000;
let logViewerSseSubscriptionCounter = 0;

function formatRelativeTime(isoStr) {
    if (!isoStr) return '';
    const ms = Date.now() - new Date(isoStr).getTime();
    if (isNaN(ms) || ms < 0) return '';
    const s = Math.floor(ms / 1000);
    if (s < 60)   return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

export class HSLogViewer extends HSComponent {
    // Component state
    #availableServices = [];
    #lockedServices = null;
    #serviceTokens = [];
    #activeServiceTokens = [];
    #serviceSearchQuery = '';
    #messageSearchQuery = '';
    #selectedServices = new Set();
    #knownServices = new Set();
    #logsCache = [];
    #logsCacheBytes = 0;
    #logIds = new Set();
    #tailActive = false;
    #tailEndTime = null;
    #tailDurationMin = 5;
    #tailTimer = null;
    #sseSubscriptionId = null;
    #sseSubscriptionVersion = null;
    #servicesRetryTimer = null;
    #serviceSearchBlurTimer = null;
    #messageTooltipEl = null;
    #activeMessageRaw = '';
    #preferPrettyJsonTooltip = true;
    #messageTooltipEnabled = true;

    // Batched grid update state
    #pendingRows = [];
    #batchFlushScheduled = false;

    // History fetch state
    #historyMode = 'lines';
    #taskInstancesService = null;  // service name whose instances are currently loaded
    #taskInstanceLoadVersion = 0;  // incremented on each load to discard stale responses
    #taskInstancesList = [];       // last-loaded task instances (for resolving "Current (running)")

    // SlickGrid state
    #dataView = null;
    #grid = null;
    #gridColumns = [
        { id: 'timestamp', name: 'Timestamp', field: 'timestamp', width: 155, formatter: SlickGridUtils.timestampFormatter, sortable: true },
        { id: 'service', name: 'Service', field: 'service', width: 140, maxLength: 20, formatter: SlickGridUtils.ellipsisFormatter, sortable: true },
        { id: 'severity', name: 'Severity', field: 'severity', width: 70, formatter: SlickGridUtils.severityFormatter, sortable: true },
        { id: 'message_plain', name: 'Message', field: 'message_plain', width: 600, maxLength: 180, formatter: SlickGridUtils.logMessagePreviewFormatter, sortable: true },
    ];

    constructor() {
        super();
        this.#dataView = SlickGridUtils.createDataView();
    }

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(styles);
        this.#bindEvents();
        this.#initGrid();
        this.#updateStatus('Loading services...');
        this.#bootstrapServiceLoad();
    }

    onUnload() {
        super.onUnload();
        this.#stopTail();
        if (this.#grid) {
            this.#grid.destroy();
            this.#grid = null;
        }
        this.#dataView = null;
        if (this.#sseSubscriptionId) {
            this.#unsubscribeSSE();
        }
        if (this.#servicesRetryTimer) {
            clearTimeout(this.#servicesRetryTimer);
            this.#servicesRetryTimer = null;
        }
        if (this.#serviceSearchBlurTimer) {
            clearTimeout(this.#serviceSearchBlurTimer);
            this.#serviceSearchBlurTimer = null;
        }
        this.#logsCache = [];
        this.#logIds = new Set();
        this.#pendingRows = [];
        this.#availableServices = [];
    }

    // ── Event Binding ──────────────────────────────────────────────────────

    #bindEvents() {
        const sd = this.shadowRoot;

        // Close
        this.onEvent('click', () => this.#close(), sd.getElementById('close-btn'));
        this.onEvent('click', () => this.#close(), sd.querySelector('.log-viewer-backdrop'));
        this.onEvent('keydown', (_, ev) => { if (ev.key === 'Escape') this.#close(); }, document);

        // Services
        this.onEvent('click', () => this.#loadAvailableServices(), sd.getElementById('service-refresh-btn'));
        this.onEvent('click', () => this.#showAllServices(), sd.getElementById('service-show-all-btn'));
        const serviceSearch = sd.getElementById('service-search');
        this.onEvent('input', () => this.#filterServiceList(serviceSearch.value), serviceSearch);
        this.onEvent('focus', () => this.#renderServiceTokenSuggestions(serviceSearch.value), serviceSearch);
        this.onEvent('blur', () => {
            this.#serviceSearchBlurTimer = setTimeout(() => {
                this.#serviceSearchBlurTimer = null;
                this.shadowRoot.getElementById('service-token-suggestions').classList.add('hidden');
            }, 160);
        }, serviceSearch);

        // Tail controls
        this.onEvent('click', () => { this.#startTail(); }, sd.getElementById('tail-start-btn'));
        this.onEvent('click', () => this.#stopTail(), sd.getElementById('tail-stop-btn'));
        this.onEvent('click', () => this.#resetTail(), sd.getElementById('tail-reset-btn'));
        this.onEvent('input', (_, ev) => this.#syncTailDurationFromInput(ev?.target), sd.getElementById('tail-duration'));
        this.onEvent('change', (_, ev) => this.#syncTailDurationFromInput(ev?.target), sd.getElementById('tail-duration'));

        // Cache
        this.onEvent('click', () => this.#clearCache(), sd.getElementById('cache-clear-btn'));

        // Export
        this.onEvent('click', () => this.#exportCsv(), sd.getElementById('export-csv-btn'));

        const messageSearchInput = sd.getElementById('message-search');
        this.onEvent('input', () => this.#onMessageSearchInput(messageSearchInput?.value), messageSearchInput);
        this.onEvent('click', () => this.#toggleMessageTooltip(), sd.getElementById('tooltip-toggle-btn'));
        this.#syncMessageTooltipToggleButton();

        // Message hover tooltip
        const tableTarget = sd.getElementById('table-target');
        this.onEvent('mouseover', (_, ev) => this.#onMessageCellHoverIn(ev), tableTarget);
        this.onEvent('mousemove', (_, ev) => this.#onMessageCellHoverMove(ev), tableTarget);
        this.onEvent('mouseout', (_, ev) => this.#onMessageCellHoverOut(ev), tableTarget);

        // History fetch controls
        this.onEvent('click', () => this.#setHistoryMode('lines'), sd.getElementById('history-mode-lines'));
        this.onEvent('click', () => this.#setHistoryMode('time'), sd.getElementById('history-mode-time'));
        this.onEvent('click', () => this.#setHistoryMode('full'), sd.getElementById('history-mode-full'));
        this.onEvent('click', () => this.#fetchHistory(), sd.getElementById('history-fetch-btn'));
        this.onEvent('click', () => this.#downloadFullLog(), sd.getElementById('history-download-btn'));
        // Keep the download button enabled state in sync with instance selection
        this.onEvent('change', () => this.#updateUIState(), sd.getElementById('history-instance-select'));
    }

    #setHistoryMode(mode) {
        const sd = this.shadowRoot;
        this.#historyMode = mode;
        const toggles = {
            lines: sd.getElementById('history-mode-lines'),
            time:  sd.getElementById('history-mode-time'),
            full:  sd.getElementById('history-mode-full'),
        };
        Object.entries(toggles).forEach(([ key, btn ]) => {
            if (btn) btn.classList.toggle('active', key === mode);
        });
        sd.getElementById('history-lines-select')?.classList.toggle('hidden', mode !== 'lines');
        sd.getElementById('history-time-range')?.classList.toggle('hidden', mode !== 'time');
        sd.getElementById('history-full-hint')?.classList.toggle('hidden', mode !== 'full');
        // Fetch is for lines/time; Download is for full only.
        sd.getElementById('history-fetch-btn')?.classList.toggle('hidden', mode === 'full');
        sd.getElementById('history-download-btn')?.classList.toggle('hidden', mode !== 'full');
        this.#updateUIState();
    }

    #ensureMessageTooltip() {
        if (this.#messageTooltipEl) {
            return this.#messageTooltipEl;
        }

        const tooltip = document.createElement('div');
        tooltip.className = 'log-message-tooltip-popup hidden';
        tooltip.addEventListener('click', (ev) => this.#onMessageTooltipAction(ev));
        tooltip.addEventListener('mouseleave', () => {
            tooltip.classList.add('hidden');
        });
        this.shadowRoot.appendChild(tooltip);
        this.#messageTooltipEl = tooltip;
        return tooltip;
    }

    #renderMessageTooltip() {
        const tooltip = this.#ensureMessageTooltip();
        tooltip.innerHTML = SlickGridUtils.buildMessageTooltipHtml(this.#activeMessageRaw, {
            preferPretty: this.#preferPrettyJsonTooltip,
        });
    }

    #positionMessageTooltip(ev) {
        if (!this.#messageTooltipEl || this.#messageTooltipEl.classList.contains('hidden')) {
            return;
        }

        const margin = 12;
        const offset = 14;
        const rect = this.#messageTooltipEl.getBoundingClientRect();
        let left = ev.clientX + offset;
        let top = ev.clientY + offset;

        if (left + rect.width > window.innerWidth - margin) {
            left = Math.max(margin, ev.clientX - rect.width - offset);
        }
        if (top + rect.height > window.innerHeight - margin) {
            top = Math.max(margin, ev.clientY - rect.height - offset);
        }

        this.#messageTooltipEl.style.left = `${left}px`;
        this.#messageTooltipEl.style.top = `${top}px`;
    }

    #findMessagePreviewInCell(el) {
        // Direct hit on the preview span.
        const direct = el?.closest?.('.hs-log-message-preview');
        if (direct) {
            return direct;
        }
        // Hit on any part of the SlickGrid cell for the message column.
        const cell = el?.closest?.('.slick-cell.l3, .slick-cell.r3');
        if (cell) {
            return cell.querySelector('.hs-log-message-preview') || null;
        }
        return null;
    }

    #onMessageCellHoverIn(ev) {
        if (!this.#messageTooltipEnabled) {
            return;
        }
        const preview = this.#findMessagePreviewInCell(ev?.target);
        if (!preview) {
            return;
        }

        const message = preview.getAttribute('data-log-message') || '';
        this.#activeMessageRaw = message;
        this.#preferPrettyJsonTooltip = true;
        const tooltip = this.#ensureMessageTooltip();
        this.#renderMessageTooltip();
        tooltip.classList.remove('hidden');
        this.#positionMessageTooltip(ev);
    }

    #onMessageCellHoverMove(ev) {
        if (!this.#messageTooltipEnabled) {
            return;
        }
        // Keep tooltip fixed after initial placement so users can select/copy text.
        if (!this.#messageTooltipEl || this.#messageTooltipEl.classList.contains('hidden')) {
            this.#positionMessageTooltip(ev);
        }
    }

    #onMessageCellHoverOut(ev) {
        if (!this.#messageTooltipEnabled) {
            return;
        }
        const fromCell = this.#findMessagePreviewInCell(ev?.target);
        const toCell = this.#findMessagePreviewInCell(ev?.relatedTarget);
        const toTooltip = ev?.relatedTarget?.closest?.('.log-message-tooltip-popup');
        if (fromCell && fromCell === toCell) {
            return;
        }
        if (toTooltip) {
            return;
        }
        if (this.#messageTooltipEl) {
            this.#messageTooltipEl.classList.add('hidden');
        }
    }

    async #copyTooltipText() {
        const contentEl = this.#messageTooltipEl?.querySelector('.hs-log-tooltip-content');
        const copyText = contentEl?.getAttribute('data-copy-text') || '';
        if (!copyText) {
            return;
        }

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(copyText);
                this.#updateStatus('Copied message to clipboard');
                return;
            }
        } catch (_err) {
            // Fall through to legacy copy method.
        }

        try {
            const tempInput = document.createElement('textarea');
            tempInput.value = copyText;
            tempInput.style.position = 'fixed';
            tempInput.style.left = '-9999px';
            document.body.appendChild(tempInput);
            tempInput.focus();
            tempInput.select();
            document.execCommand('copy');
            document.body.removeChild(tempInput);
            this.#updateStatus('Copied message to clipboard');
        } catch (_err) {
            this.#updateStatus('Unable to copy message from tooltip');
        }
    }

    #onMessageTooltipAction(ev) {
        const button = ev?.target?.closest?.('[data-tooltip-action]');
        if (!button) {
            return;
        }

        const action = button.getAttribute('data-tooltip-action');
        if (action === 'toggle-view') {
            this.#preferPrettyJsonTooltip = !this.#preferPrettyJsonTooltip;
            this.#renderMessageTooltip();
            return;
        }

        if (action === 'copy') {
            this.#copyTooltipText();
        }
    }

    #hideMessageTooltip() {
        if (this.#messageTooltipEl) {
            this.#messageTooltipEl.classList.add('hidden');
        }
    }

    #syncMessageTooltipToggleButton() {
        const btn = this.shadowRoot?.getElementById('tooltip-toggle-btn');
        if (!btn) {
            return;
        }
        btn.setAttribute('aria-pressed', this.#messageTooltipEnabled ? 'true' : 'false');
        btn.textContent = this.#messageTooltipEnabled ? 'Tooltip: On' : 'Tooltip: Off';
        btn.title = this.#messageTooltipEnabled
            ? 'Disable message tooltip popup'
            : 'Enable message tooltip popup';
        btn.classList.toggle('is-active', this.#messageTooltipEnabled);
    }

    #toggleMessageTooltip() {
        this.#messageTooltipEnabled = !this.#messageTooltipEnabled;
        if (!this.#messageTooltipEnabled) {
            this.#hideMessageTooltip();
        }
        this.#syncMessageTooltipToggleButton();
        this.#updateStatus(this.#messageTooltipEnabled ? 'Message tooltip enabled' : 'Message tooltip disabled');
    }

    #onMessageSearchInput(value) {
        this.#messageSearchQuery = String(value || '').trim().toLowerCase();
        this.#applyFilters();
    }

    // ── Grid Initialization ────────────────────────────────────────────────

    #initGrid() {
        requestAnimationFrame(() => {
            if (!this.isConnected) {
                Logger.debug('Skipping grid init for detached log viewer');
                return;
            }

            const parentNode = this.shadowRoot.getElementById('table-target');
            if (!parentNode) {
                Logger.error('No parent node found for log viewer grid');
                return;
            }

            this.#grid = SlickGridUtils.initGrid(parentNode, this.#dataView, this.#gridColumns, {
                shadowRoot: this.shadowRoot,
            });

            SlickGridUtils.setupGridEvents(this.#grid, this.#dataView, (sortAsc, sortCols) => {
                const { sortCol, sortAsc: colSortAsc } = sortCols[0];
                const field = sortCol.field;
                this.#dataView.sort((a, b) => {
                    const va = a[field] ?? '';
                    const vb = b[field] ?? '';
                    const cmp = va < vb ? -1 : va > vb ? 1 : 0;
                    return colSortAsc ? cmp : -cmp;
                }, sortAsc);
                this.#grid.invalidate();
                this.#grid.render();
            }, null);
            this.#grid.resizeCanvas();
            this.#applyFilters();
        });
    }

    // ── Service Management ─────────────────────────────────────────────────

    #bootstrapServiceLoad() {
        const maxAttempts = 5;
        const retryMs = 1500;
        let attempts = 0;

        const attemptLoad = async () => {
            attempts += 1;
            const ok = await this.#loadAvailableServices();
            if (ok || attempts >= maxAttempts || !this.isConnected) {
                return;
            }
            this.#servicesRetryTimer = setTimeout(() => {
                this.#servicesRetryTimer = null;
                attemptLoad();
            }, retryMs);
        };

        attemptLoad();
    }

    async #loadAvailableServices() {
        try {
            const response = await this.fetch('/api/live/services');
            const services = Array.isArray(response) ? response : [];

            this.#availableServices = services;
            // Preserve existing selections; remove services that no longer exist
            this.#selectedServices = new Set(
                Array.from(this.#selectedServices).filter(s => this.#availableServices.includes(s))
            );
            this.#knownServices = new Set(this.#availableServices);
            this.#serviceTokens = buildRankedTokens(this.#availableServices, {
                getItemKey: (service, index) => `${service || ''}:${index}`,
                getSearchText: service => service,
                minTokenLength: 2,
            });
            Logger.debug('Loaded available services:', this.#availableServices);
            this.#renderServiceTokenPills();
            this.#renderServiceTokenSuggestions(this.#serviceSearchQuery);
            this.#redrawServices();
            this.#updateUIState();
            if (this.#grid) {
                this.#applyFilters();
            }
            if (this.#availableServices.length > 0) {
                if (!this.#tailActive && this.#lockedServices === null) {
                    this.#updateStatus(`${this.#availableServices.length} service(s) available — select services and click Start`);
                }
                return true;
            }
            this.#updateStatus('No services available yet, retrying...');
            return false;
        } catch (err) {
            Logger.error('Failed to load available services:', err);
            const message = err?.message ? `Failed to load services: ${err.message}` : 'Failed to load services, retrying...';
            this.#updateStatus(message);
            return false;
        }
    }

    #useDockerMode() {
        const userinfo = UserSession.get('userinfo');
        const hasDocker = hasCapability(userinfo, 'live.logs.mode.docker');
        const hasKafka = hasCapability(userinfo, 'live.logs.mode.kafka');
        const preferDocker = hasCapability(userinfo, 'live.logs.mode.prefer_docker');
        if (!hasDocker) {
            return false;
        }
        if (preferDocker) {
            return true;
        }
        return !hasKafka;
    }

    #redrawServices() {
        const sd = this.shadowRoot;
        const serviceList = sd.getElementById('service-list');
        serviceList.innerHTML = '';
        const filteredServices = this.#getFilteredServices();

        if (filteredServices.length < 1) {
            const empty = document.createElement('div');
            empty.className = 'service-list-empty';
            empty.textContent = this.#availableServices.length < 1 ? 'No services available' : 'No services match your search';
            serviceList.appendChild(empty);
            return;
        }

        const singleServiceMode = this.#useDockerMode();
        filteredServices.forEach(service => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'service-item' + (this.#selectedServices.has(service) ? ' selected' : '');
            btn.textContent = service;

            btn.addEventListener('click', () => {
                if (singleServiceMode) {
                    // Radio behaviour: only one service at a time in docker mode.
                    this.#selectedServices = new Set([service]);
                    serviceList.querySelectorAll('.service-item').forEach(b => b.classList.remove('selected'));
                    btn.classList.add('selected');
                } else if (this.#selectedServices.has(service)) {
                    this.#selectedServices.delete(service);
                    btn.classList.remove('selected');
                } else {
                    this.#selectedServices.add(service);
                    btn.classList.add('selected');
                }
                if (this.#grid) {
                    this.#applyFilters();
                }
                this.#updateUIState();
            });

            serviceList.appendChild(btn);
        });
    }

    #getFilteredServices() {
        const sourceList = this.#lockedServices !== null
            ? Array.from(this.#lockedServices)
            : this.#availableServices;
        return sourceList.filter(service => {
            if (!itemMatchesActiveTokens(service, this.#activeServiceTokens, x => x)) {
                return false;
            }
            return itemMatchesQuery(service, this.#serviceSearchQuery, x => x);
        });
    }

    #getSuggestedServiceTokens(query) {
        return getSuggestedTokens(this.#serviceTokens, this.#activeServiceTokens, query, {
            minQueryLength: 2,
            maxSuggestions: 6,
        });
    }

    #renderServiceTokenPills() {
        const container = this.shadowRoot.getElementById('service-token-pills');
        if (!container) {
            return;
        }
        container.innerHTML = '';
        this.#activeServiceTokens.forEach(token => {
            const pill = document.createElement('button');
            pill.type = 'button';
            pill.className = 'service-token-pill';
            pill.innerHTML = `<span>${token}</span><span class="service-token-pill-remove">x</span>`;
            pill.addEventListener('click', () => this.#removeServiceToken(token));
            container.appendChild(pill);
        });
        container.classList.toggle('hidden', this.#activeServiceTokens.length < 1);
    }

    #renderServiceTokenSuggestions(query) {
        const container = this.shadowRoot.getElementById('service-token-suggestions');
        if (!container) {
            return;
        }
        container.innerHTML = '';
        const suggestions = this.#getSuggestedServiceTokens(query);
        suggestions.forEach(token => {
            const pill = document.createElement('button');
            pill.type = 'button';
            pill.className = 'service-token-suggestion';
            pill.textContent = token;
            pill.addEventListener('mousedown', (ev) => {
                ev.preventDefault();
                this.#addServiceToken(token);
            });
            container.appendChild(pill);
        });
        container.classList.toggle('hidden', suggestions.length < 1);
    }

    #addServiceToken(token) {
        const normalized = String(token || '').trim().toLowerCase();
        if (!normalized || this.#activeServiceTokens.includes(normalized)) {
            return;
        }
        this.#activeServiceTokens = [ ...this.#activeServiceTokens, normalized ];
        const serviceSearch = this.shadowRoot.getElementById('service-search');
        if (serviceSearch) {
            serviceSearch.value = '';
            serviceSearch.focus();
        }
        this.#serviceSearchQuery = '';
        this.#renderServiceTokenPills();
        this.#renderServiceTokenSuggestions('');
        this.#redrawServices();
        if (this.#grid) {
            this.#applyFilters();
        }
    }

    #removeServiceToken(token) {
        this.#activeServiceTokens = this.#activeServiceTokens.filter(x => x !== token);
        this.#renderServiceTokenPills();
        const query = this.shadowRoot.getElementById('service-search')?.value || this.#serviceSearchQuery;
        this.#renderServiceTokenSuggestions(query);
        this.#redrawServices();
        if (this.#grid) {
            this.#applyFilters();
        }
    }

    #filterServiceList(query) {
        this.#serviceSearchQuery = String(query || '').trim().toLowerCase();
        this.#renderServiceTokenSuggestions(this.#serviceSearchQuery);
        this.#redrawServices();
        if (this.#grid) {
            this.#applyFilters();
        }
    }

    #showAllServices() {
        const allServices = this.#lockedServices ?? this.#availableServices;
        if (this.#useDockerMode()) {
            const first = Array.isArray(allServices) ? allServices[0] : Array.from(allServices)[0];
            this.#selectedServices = first ? new Set([first]) : new Set();
        } else {
            this.#selectedServices = new Set(allServices);
        }
        this.#activeServiceTokens = [];
        this.#serviceSearchQuery = '';

        const serviceSearch = this.shadowRoot.getElementById('service-search');
        if (serviceSearch) {
            serviceSearch.value = '';
        }

        const suggestions = this.shadowRoot.getElementById('service-token-suggestions');
        if (suggestions) {
            suggestions.classList.add('hidden');
        }

        this.#renderServiceTokenPills();
        this.#redrawServices();
        if (this.#grid) {
            this.#applyFilters();
        }
        this.#updateStatus('Showing all services');
    }

    #serviceMatchesCurrentSearch(service) {
        if (!itemMatchesActiveTokens(service, this.#activeServiceTokens, x => x)) {
            return false;
        }
        return itemMatchesQuery(service, this.#serviceSearchQuery, x => x);
    }

    // ── Tail Control ───────────────────────────────────────────────────────

    #syncTailDurationFromInput(input) {
        const target = input || this.shadowRoot.getElementById('tail-duration');
        if (!target) {
            return this.#tailDurationMin;
        }

        this.#tailDurationMin = Math.max(1, Math.min(5, parseInt(target.value, 10) || 5));
        target.value = String(this.#tailDurationMin);
        return this.#tailDurationMin;
    }

    async #startTail() {
        if (this.#tailActive) {
            Logger.warn('Tail already active');
            return;
        }

        // Lock in services if not already locked (first Run from selection phase)
        if (this.#lockedServices === null) {
            if (this.#useDockerMode()) {
                if (this.#selectedServices.size !== 1) {
                    this.#updateStatus('Select exactly one service to stream');
                    return;
                }
            } else if (this.#selectedServices.size === 0) {
                this.#updateStatus('Select at least one service, then click Start');
                return;
            }
            this.#lockedServices = new Set(this.#selectedServices);
        }

        // Reset client-side selection to all locked services for a clean start
        this.#selectedServices = new Set(this.#lockedServices);
        this.#redrawServices();

        this.#syncTailDurationFromInput();
        this.#tailActive = true;
        this.#tailEndTime = Date.now() + (this.#tailDurationMin * 60 * 1000);
        if (this.#useDockerMode()) {
            // Docker mode: single-service follow stream (includes recent logs via tail=N)
            await this.#subscribeToDockerStream();
        } else {
            // Kafka mode: multi-service filter
            await this.#prefetchRecentLogs();
            this.#subscribeToLogs();
        }
        this.#startTailTimer();
        this.#updateUIState();
    }

    async #subscribeToDockerStream() {
        try {
            if (!this.#sseSubscriptionId) {
                logViewerSseSubscriptionCounter += 1;
                this.#sseSubscriptionId = `log-viewer-${logViewerSseSubscriptionCounter}`;
            }
            await this.#subscribeToDockerStreamWithReconnect();
            const service = Array.from(this.#lockedServices)[0];
            this.#updateStatus(`Streaming logs for ${service}...`);
            Logger.info('Started Docker follow log stream:', { service, subscriptionId: this.#sseSubscriptionId });
        } catch (err) {
            Logger.error('Failed to start Docker follow log stream:', err);
            this.#updateStatus('Failed to start log stream');
            this.#tailActive = false;
            this.#updateUIState();
        }
    }

    async #subscribeToDockerStreamWithReconnect() {
        try {
            await this.#subscribeToDockerStreamOnce();
        } catch (err) {
            if (!this.#isMissingSseSessionError(err)) throw err;
            Logger.warn('Docker log stream: stale SSE session, reconnecting once', {
                previousStreamId: UserSession.get('sseStreamId'),
                subscriptionId: this.#sseSubscriptionId,
            });
            UserSession.set('sseStreamId', null);
            await Auth.sseConnect();
            await this.#subscribeToDockerStreamOnce();
        }
    }

    async #subscribeToDockerStreamOnce() {
        let sseStreamId = UserSession.get('sseStreamId');
        if (!sseStreamId) {
            await Auth.sseConnect();
            sseStreamId = UserSession.get('sseStreamId');
        }
        if (!sseStreamId) throw new Error('SSE session not connected');

        const service = Array.from(this.#lockedServices)[0];
        const path = `/api/live/docker/service_logs/stream?subscribe=${sseStreamId}:${this.#sseSubscriptionId}&service=${encodeURIComponent(service)}`;

        const subscriptions = UserSession.get('sseSubscriptions') || {};
        subscriptions[this.#sseSubscriptionId] = {
            onSubscribed: this.#onSubscribed.bind(this),
            onUpdate: this.#onLogUpdate.bind(this),
            resubscribe: async (activeStreamId) => {
                const nextPath = `/api/live/docker/service_logs/stream?subscribe=${activeStreamId}:${this.#sseSubscriptionId}&service=${encodeURIComponent(service)}`;
                await this.fetch(nextPath);
            },
        };
        UserSession.set('sseSubscriptions', subscriptions);
        await this.fetch(path);
    }

    async #prefetchRecentLogs() {
        this.#setPrefetchProgress(true, 0, 1, 'Prefetching recent logs...');
        try {
            if (!this.#tailActive) {
                return;
            }

            const prefetched = await this.#fetchPrefetchChunk();
            const newLogs = [];
            prefetched.forEach(log => {
                const id = log?.id || `${log?.kafka_topic || 'topic'}:${log?.kafka_partition || 0}:${log?.kafka_offset || 0}`;
                if (this.#logIds.has(id)) {
                    return;
                }
                this.#logIds.add(id);
                if (!log.id) {
                    log.id = id;
                }
                this.#pushLogToCache(log);
                newLogs.push(log);
            });

            if (newLogs.length > 0) {
                SlickGridUtils.addRows(this.#dataView, newLogs);
                this.#enforceMemoryLimit();
                this.#updateLogCount();
                this.#checkMemoryUsage();
                this.#updateStatus(`Loaded ${newLogs.length} recent log(s), tailing live...`);
            }
        } catch (err) {
            Logger.warn('Failed to prefetch recent logs for live tail start:', err);
        } finally {
            this.#setPrefetchProgress(false, 1, 1, '');
        }
    }

    async #fetchPrefetchChunk() {
        let url = `/api/live/kafka/service_logs/prefetch?limit=${PREFETCH_LIMIT}`;
        if (this.#lockedServices && this.#lockedServices.size > 0) {
            url += `&services=${encodeURIComponent(Array.from(this.#lockedServices).join(','))}`;
        }
        const rows = await this.fetch(url);
        return Array.isArray(rows) ? rows : [];
    }

    #setPrefetchProgress(active, completed, total, message) {
        const sd = this.shadowRoot;
        const progressEl = sd.getElementById('prefetch-progress');
        const progressText = sd.getElementById('prefetch-progress-text');
        const progressFill = sd.getElementById('prefetch-progress-fill');
        if (!progressEl || !progressText || !progressFill) {
            return;
        }

        const safeTotal = Math.max(1, Number(total) || 1);
        const safeCompleted = Math.max(0, Math.min(safeTotal, Number(completed) || 0));
        const pct = Math.round((safeCompleted / safeTotal) * 100);

        progressText.textContent = message || `Prefetching logs (${safeCompleted}/${safeTotal})...`;
        progressFill.style.width = `${pct}%`;
        progressEl.classList.toggle('hidden', !active);
    }

    #stopTail() {
        if (!this.#tailActive) return;

        this.#tailActive = false;
        if (this.#tailTimer) {
            clearInterval(this.#tailTimer);
            this.#tailTimer = null;
        }
        this.#unsubscribeSSE();
        this.#flushPendingRows();
        this.#setPrefetchProgress(false, 0, 1, '');
        this.#updateUIState();
        this.#updateStatus('Tail stopped');
    }

    #resetTail() {
        if (this.#tailActive) {
            this.#stopTail();
        }
        this.#lockedServices = null;
        this.#selectedServices = new Set();
        this.#knownServices = new Set();
        this.#logIds = new Set();
        this.#logsCache = [];
        this.#logsCacheBytes = 0;
        this.#pendingRows = [];
        this.#batchFlushScheduled = false;
        SlickGridUtils.clearRows(this.#dataView);
        this.#redrawServices();
        this.#updateLogCount();
        this.#updateUIState();
        const sd = this.shadowRoot;
        if (sd) sd.getElementById('cache-size').textContent = '0 MB';
        this.#updateStatus(`${this.#availableServices.length} service(s) available — select services and click Start`);
    }

    #startTailTimer() {
        const sd = this.shadowRoot;
        const timerEl = sd.getElementById('tail-timer');

        const renderRemaining = () => {
            const remaining = Math.max(0, this.#tailEndTime - Date.now());
            const mins = Math.floor(remaining / 60000);
            const secs = Math.floor((remaining % 60000) / 1000);
            timerEl.textContent = `${mins}:${secs.toString().padStart(2, '0')}`;

            if (remaining <= 0) {
                this.#stopTail();
            }
        };

        renderRemaining();

        this.#tailTimer = setInterval(() => {
            renderRemaining();
        }, 1000);
    }

    // ── SSE Subscription ───────────────────────────────────────────────────

    async #subscribeToLogs() {
        try {
            if (!this.#sseSubscriptionId) {
                logViewerSseSubscriptionCounter += 1;
                this.#sseSubscriptionId = `log-viewer-${logViewerSseSubscriptionCounter}`;
            }

            await this.#subscribeToLogsWithReconnect();

            this.#updateStatus(`Tailing ${(this.#lockedServices ?? this.#selectedServices).size} service(s)...`);
            Logger.info('Subscribed to live logs:', {
                services: Array.from(this.#lockedServices ?? this.#selectedServices),
                sseStreamId: UserSession.get('sseStreamId'),
                subscriptionId: this.#sseSubscriptionId,
            });
        } catch (err) {
            Logger.error('Failed to subscribe to live logs:', err);
            this.#updateStatus('Failed to subscribe to logs');
            this.#tailActive = false;
            this.#updateUIState();
        }
    }

    async #subscribeToLogsWithReconnect() {
        try {
            await this.#subscribeToLogsOnce();
        } catch (err) {
            if (!this.#isMissingSseSessionError(err)) {
                throw err;
            }

            Logger.warn('Live log subscribe found stale SSE session, reconnecting and retrying once', {
                previousStreamId: UserSession.get('sseStreamId'),
                subscriptionId: this.#sseSubscriptionId,
            });

            UserSession.set('sseStreamId', null);
            await Auth.sseConnect();
            await this.#subscribeToLogsOnce();
        }
    }

    async #subscribeToLogsOnce() {
        let sseStreamId = UserSession.get('sseStreamId');
        if (!sseStreamId) {
            await Auth.sseConnect();
            sseStreamId = UserSession.get('sseStreamId');
        }
        if (!sseStreamId) {
            throw new Error('SSE session not connected');
        }

        const filter = this.#buildServiceFilter();
        let path = `/api/live/kafka/service_logs?subscribe=${sseStreamId}:${this.#sseSubscriptionId}&subscribe_only=true`;
        if (filter) {
            path += `&filter=${JSON.stringify(filter)}`;
        }

        Logger.debug('Preparing live log subscription', {
            lockedServices: this.#lockedServices ? Array.from(this.#lockedServices) : null,
            filter,
            path,
        });

        const subscriptions = UserSession.get('sseSubscriptions') || {};
        subscriptions[this.#sseSubscriptionId] = {
            onSubscribed: this.#onSubscribed.bind(this),
            onUpdate: this.#onLogUpdate.bind(this),
            resubscribe: async (activeStreamId) => {
                const nextFilter = this.#buildServiceFilter();
                let nextPath = `/api/live/kafka/service_logs?subscribe=${activeStreamId}:${this.#sseSubscriptionId}&subscribe_only=true`;
                if (nextFilter) {
                    nextPath += `&filter=${JSON.stringify(nextFilter)}`;
                }
                await this.fetch(nextPath);
            },
        };
        UserSession.set('sseSubscriptions', subscriptions);

        await this.fetch(path);
    }

    #isMissingSseSessionError(err) {
        return err?.response?.status === 404;
    }

    #unsubscribeSSE() {
        if (!this.#sseSubscriptionId) return;

        try {
            const sseStreamId = UserSession.get('sseStreamId');
            this.fetch(`/sse?unsubscribe=${sseStreamId}:${this.#sseSubscriptionId}`).catch(err => {
                Logger.warn('Failed to unsubscribe from SSE:', err);
            });

            const subscriptions = UserSession.get('sseSubscriptions') || {};
            delete subscriptions[this.#sseSubscriptionId];
            UserSession.set('sseSubscriptions', subscriptions);
        } finally {
            this.#sseSubscriptionId = null;
            this.#sseSubscriptionVersion = null;
        }
    }

    #buildServiceFilter() {
        if (!this.#lockedServices) return null;
        const services = Array.from(this.#lockedServices).filter(Boolean);
        if (services.length === 0) return null;
        return [ '${obj.service}', 'in', services ];
    }

    #onSubscribed(subscription) {
        Logger.info('got SSE subscribed message for log viewer: ', subscription);
        this.#sseSubscriptionVersion = subscription.subscription_version;
    }

    #onLogUpdate(update) {
        if (!update || typeof update !== 'object') {
            Logger.warn('Invalid log update:', update);
            return;
        }

        if (update.subscription_version !== this.#sseSubscriptionVersion) {
            Logger.warn('Discarding old SSE versioned data update for log viewer: ', update);
            return;
        }

        const logs = update.obj ? [ update.obj ] : (Array.isArray(update) ? update : [update]);
        const newRows = [];
        logs.forEach(log => {
            if (!log.id) {
                log.id = update.id || `${log.kafka_topic}:${log.kafka_partition}:${log.kafka_offset}`;
            }
            if (this.#logIds.has(log.id)) {
                return;
            }
            this.#logIds.add(log.id);
            this.#pushLogToCache(log);
            newRows.push(log);
        });

        this.#enforceMemoryLimit();

        if (newRows.length < 1) {
            return;
        }

        for (const row of newRows) {
            this.#pendingRows.push(row);
        }
        this.#scheduleBatchFlush();
    }

    #enforceMemoryLimit() {
        if (this.#logsCache.length <= MAX_LOGS_IN_MEMORY) {
            return;
        }

        const toRemove = this.#logsCache.length - MAX_LOGS_IN_MEMORY;
        const removed = this.#logsCache.splice(0, toRemove);
        removed.forEach(packed => {
            if (Array.isArray(packed) && packed[0]) {
                this.#logIds.delete(packed[0]);
            }
            this.#logsCacheBytes -= this.#estimatePackedEntryBytes(packed);
        });
        if (this.#logsCacheBytes < 0) {
            this.#logsCacheBytes = 0;
        }
    }

    #scheduleBatchFlush() {
        if (this.#batchFlushScheduled) return;
        this.#batchFlushScheduled = true;
        requestAnimationFrame(() => this.#flushPendingRows());
    }

    #flushPendingRows() {
        this.#batchFlushScheduled = false;
        const rows = this.#pendingRows;
        if (rows.length === 0) return;
        this.#pendingRows = [];
        SlickGridUtils.addRows(this.#dataView, rows);
        this.#updateLogCount();
        this.#checkMemoryUsage();
    }

    #packLogForCache(log) {
        return [
            String(log?.id || ''),
            String(log?.timestamp || ''),
            String(log?.service || ''),
            String(log?.severity || ''),
            String(log?.message_plain || log?.message || ''),
        ];
    }

    #estimatePackedEntryBytes(packed) {
        if (!Array.isArray(packed)) {
            return 0;
        }
        let bytes = 16;
        for (const value of packed) {
            bytes += (String(value || '').length * 2) + 8;
        }
        return bytes;
    }

    #pushLogToCache(log) {
        const packed = this.#packLogForCache(log);
        this.#logsCache.push(packed);
        this.#logsCacheBytes += this.#estimatePackedEntryBytes(packed);
    }

    #applyFilters() {
        SlickGridUtils.applyFilter(this.#dataView, (row) => {
            const service = String(row?.service || '');

            // Apply token/text service search as a display filter for both list and logs
            if (!this.#serviceMatchesCurrentSearch(service)) {
                return false;
            }

            // Apply explicit service toggle selection
            if (this.#knownServices.has(service) && !this.#selectedServices.has(service)) {
                return false;
            }

            if (this.#messageSearchQuery) {
                const text = String(row?.message_plain || row?.message || '').toLowerCase();
                if (!text.includes(this.#messageSearchQuery)) {
                    return false;
                }
            }

            return true;
        });

        // Force immediate repaint so filtering feels progressive while typing.
        if (this.#dataView && typeof this.#dataView.refresh === 'function') {
            this.#dataView.refresh();
        }
        if (this.#grid) {
            this.#grid.invalidate();
            this.#grid.render();
        }
    }

    // ── Memory Management ──────────────────────────────────────────────────

    #checkMemoryUsage() {
        const estimatedMB = this.#logsCacheBytes / (1024 * 1024);
        const sd = this.shadowRoot;
        sd.getElementById('cache-size').textContent = estimatedMB.toFixed(1) + ' MB';

        if (estimatedMB > MEMORY_WARNING_MB) {
            const proceed = confirm(
                `Log cache is using ${estimatedMB.toFixed(1)} MB of memory. ` +
                `Clear the cache to continue receiving new logs?`
            );
            if (proceed) {
                this.#clearCache();
            }
        }
    }

    #clearCache() {
        this.#logsCache = [];
        this.#logsCacheBytes = 0;
        this.#logIds.clear();
        SlickGridUtils.clearRows(this.#dataView);
        this.#updateLogCount();

        const sd = this.shadowRoot;
        sd.getElementById('cache-size').textContent = '0 MB';
        this.#updateStatus('Cache cleared');
    }

    // ── UI Updates ─────────────────────────────────────────────────────────

    // ── Task Instance Management ───────────────────────────────────────────

    async #loadTaskInstances(service) {
        const sd = this.shadowRoot;
        const instanceSelect = sd.getElementById('history-instance-select');
        if (!instanceSelect) return;

        // Mark immediately so #updateUIState doesn't re-trigger while awaiting
        this.#taskInstancesService = service;
        const version = ++this.#taskInstanceLoadVersion;

        instanceSelect.innerHTML = '';
        const loadingOpt = document.createElement('option');
        loadingOpt.value = '';
        loadingOpt.textContent = 'Loading instances…';
        instanceSelect.appendChild(loadingOpt);
        instanceSelect.disabled = true;

        try {
            const params = new URLSearchParams({ service });
            const tasks = await this.fetch(`/api/live/docker/service_logs/instances?${params}`);
            if (version !== this.#taskInstanceLoadVersion) return;  // service changed mid-flight
            if (!Array.isArray(tasks)) throw new Error('Unexpected response format');
            this.#renderTaskInstanceOptions(tasks);
        } catch (err) {
            if (version !== this.#taskInstanceLoadVersion) return;
            Logger.warn('Failed to load task instances:', err);
            this.#taskInstancesList = [];
            instanceSelect.innerHTML = '';
            const fallbackOpt = document.createElement('option');
            fallbackOpt.value = '';
            fallbackOpt.textContent = 'Current (running)';
            instanceSelect.appendChild(fallbackOpt);
            const historyEnabled = (this.#lockedServices ?? this.#selectedServices).size === 1 && !this.#tailActive;
            instanceSelect.disabled = !historyEnabled;
        }
    }

    #renderTaskInstanceOptions(tasks) {
        const sd = this.shadowRoot;
        const instanceSelect = sd.getElementById('history-instance-select');
        if (!instanceSelect) return;

        this.#taskInstancesList = Array.isArray(tasks) ? tasks : [];
        instanceSelect.innerHTML = '';
        const defaultOpt = document.createElement('option');
        defaultOpt.value = '';
        defaultOpt.textContent = 'Current (running)';
        instanceSelect.appendChild(defaultOpt);

        for (const task of tasks) {
            const icon = task.state === 'running' ? '\u25cf'
                : (task.state === 'failed' || task.state === 'rejected') ? '\u2715'
                : '\u25cb';
            const state = task.state.charAt(0).toUpperCase() + task.state.slice(1);
            const slot  = task.slot != null ? `.${task.slot}` : '';
            const age   = formatRelativeTime(task.updated_at || task.created_at);
            const err   = task.error ? ` \u2014 ${task.error.slice(0, 50)}` : '';
            const opt = document.createElement('option');
            opt.value = task.id;
            opt.textContent = `${icon} ${state} \u2014 ${task.short_id}${slot}  (${age})${err}`;
            instanceSelect.appendChild(opt);
        }

        const historyEnabled = (this.#lockedServices ?? this.#selectedServices).size === 1 && !this.#tailActive;
        instanceSelect.disabled = !historyEnabled;
    }

    #updateUIState() {
        const sd = this.shadowRoot;
        const startBtn = sd.getElementById('tail-start-btn');
        const stopBtn = sd.getElementById('tail-stop-btn');
        const resetBtn = sd.getElementById('tail-reset-btn');
        const timerEl = sd.getElementById('tail-timer');
        const statusEl = sd.getElementById('tail-status');
        const exportBtn = sd.getElementById('export-csv-btn');

        // History bar: fetch button enabled when exactly 1 service is selected and not tailing
        const effectiveServices = this.#lockedServices ?? this.#selectedServices;
        const singleServiceSelected = effectiveServices.size === 1;
        const historyEnabled = singleServiceSelected && !this.#tailActive;
        const historyFetchBtn = sd.getElementById('history-fetch-btn');
        if (historyFetchBtn) historyFetchBtn.disabled = !historyEnabled;
        const historyStatus = sd.getElementById('history-status');
        if (historyStatus && !historyEnabled && historyStatus.textContent === '') {
            historyStatus.textContent = '';
        }

        // Download button (Full mode only): requires a single service, not
        // tailing, and a resolvable task instance. "Current (running)" resolves
        // to the first running task. Always downloads the entire log via a
        // native browser download (works across browsers).
        const downloadBtn = sd.getElementById('history-download-btn');
        if (downloadBtn) {
            const taskId = this.#resolveDownloadTaskId();
            const downloadEnabled = historyEnabled && !!taskId;
            downloadBtn.disabled = !downloadEnabled;

            if (!historyEnabled) {
                downloadBtn.title = 'Select a single service (and stop tailing) to download';
            } else if (!taskId) {
                downloadBtn.title = 'No instance available to download';
            } else {
                downloadBtn.title = `Stream the entire log for instance ${taskId.slice(0, 12)} directly to a file`;
            }
        }

        // Instance selector: load when a single service is selected; clear otherwise.
        // Loading happens regardless of tailActive so the user can browse instances
        // even while tailing (they can then fetch after stopping).
        const instanceSelect = sd.getElementById('history-instance-select');
        if (instanceSelect) {
            const singleService = singleServiceSelected ? Array.from(effectiveServices)[0] : null;
            if (singleService !== this.#taskInstancesService) {
                if (singleService) {
                    this.#loadTaskInstances(singleService);  // async; sets #taskInstancesService immediately
                } else {
                    this.#taskInstanceLoadVersion++;  // cancel any in-flight load
                    this.#taskInstancesService = null;
                    this.#taskInstancesList = [];
                    instanceSelect.innerHTML = '';
                    const opt = document.createElement('option');
                    opt.value = '';
                    opt.textContent = 'Current (running)';
                    instanceSelect.appendChild(opt);
                    instanceSelect.disabled = true;
                }
            }
            // Always sync enabled state to historyEnabled (e.g. after tail starts/stops)
            if (this.#taskInstancesService !== null) {
                instanceSelect.disabled = !historyEnabled;
            }
        }

        if (this.#tailActive) {
            // Running state
            startBtn.classList.add('hidden');
            stopBtn.classList.remove('hidden');
            if (resetBtn) resetBtn.classList.add('hidden');
            timerEl.classList.remove('hidden');
            statusEl.textContent = `Tailing ${(this.#lockedServices ?? this.#selectedServices).size} service(s)...`;
            if (exportBtn) exportBtn.disabled = true;
        } else if (this.#lockedServices !== null) {
            // Stopped state — can run again or reset
            startBtn.classList.remove('hidden');
            startBtn.disabled = false;
            startBtn.textContent = 'Run Again';
            stopBtn.classList.add('hidden');
            if (resetBtn) resetBtn.classList.remove('hidden');
            timerEl.classList.add('hidden');
            statusEl.textContent = 'Ready';
            if (exportBtn) exportBtn.disabled = false;
        } else {
            // Selection state — pick services before starting
            startBtn.classList.remove('hidden');
            startBtn.disabled = this.#useDockerMode()
                ? this.#selectedServices.size !== 1
                : this.#selectedServices.size === 0;
            startBtn.textContent = 'Start';
            stopBtn.classList.add('hidden');
            if (resetBtn) resetBtn.classList.add('hidden');
            timerEl.classList.add('hidden');
            if (this.#useDockerMode()) {
                statusEl.textContent = this.#selectedServices.size !== 1
                    ? 'Select one service to stream'
                    : `Streaming: ${Array.from(this.#selectedServices)[0]}`;
            } else {
                statusEl.textContent = this.#selectedServices.size === 0
                    ? 'Select services to begin'
                    : `${this.#selectedServices.size} service(s) selected`;
            }
            if (exportBtn) exportBtn.disabled = true;
        }
    }

    async #fetchHistory() {
        const sd = this.shadowRoot;
        const fetchBtn = sd.getElementById('history-fetch-btn');
        const historyStatus = sd.getElementById('history-status');

        const effectiveServices = this.#lockedServices ?? this.#selectedServices;
        if (effectiveServices.size !== 1 || this.#tailActive) return;
        const service = Array.from(effectiveServices)[0];

        const taskId = sd.getElementById('history-instance-select')?.value || '';

        try {
            const params = new URLSearchParams({ service });
            if (this.#historyMode === 'time') {
                // Time-range mode is task-scoped only — require a selected instance.
                if (!taskId) {
                    historyStatus.textContent = 'Select an instance to fetch a time range';
                    return;
                }
                const range = this.#readTimeRange();
                if (range.error) {
                    historyStatus.textContent = range.error;
                    return;
                }
                if (range.since !== null) params.set('since', String(range.since));
                if (range.until !== null) params.set('until', String(range.until));
            } else {
                const tail = Number(sd.getElementById('history-lines-select').value) || 1000;
                params.set('tail', String(tail));
            }
            if (taskId) {
                params.set('task_id', taskId);
            }

            fetchBtn.disabled = true;
            historyStatus.textContent = 'Fetching…';

            const rows = await this.fetch(`/api/live/docker/service_logs/history?${params.toString()}`);
            if (!Array.isArray(rows)) throw new Error('Unexpected response format');

            // Replace all grid content with history rows
            this.#dataView.setItems([]);
            this.#logsCache = [];
            this.#logsCacheBytes = 0;
            this.#logIds = new Set();
            this.#pendingRows = [];

            if (rows.length > 0) {
                this.#dataView.setItems(rows);
            }
            this.#grid.invalidate();
            this.#grid.render();
            this.#updateLogCount();
            const plural = rows.length !== 1 ? 's' : '';
            if (rows.length >= 10000) {
                historyStatus.textContent = `Loaded ${rows.length.toLocaleString()} lines — 10,000-line limit reached; use “Download full log” for everything`;
            } else {
                historyStatus.textContent = `Loaded ${rows.length.toLocaleString()} line${plural}`;
            }
        } catch (err) {
            Logger.warn('Failed to fetch Docker log history:', err);
            historyStatus.textContent = `Error: ${err?.message || 'fetch failed'}`;
        } finally {
            fetchBtn.disabled = false;
        }
    }

    /**
     * Read the begin/end datetime-local inputs and convert to unix epoch seconds
     * (browser-local time). Returns { since, until, error }. Either bound may be
     * null (open-ended). Validates that begin <= end when both are present.
     */
    #readTimeRange() {
        const sd = this.shadowRoot;
        const beginVal = sd.getElementById('history-begin-input')?.value || '';
        const endVal = sd.getElementById('history-end-input')?.value || '';
        const toEpoch = (val) => {
            if (!val) return null;
            const ms = new Date(val).getTime();
            return Number.isFinite(ms) ? Math.floor(ms / 1000) : NaN;
        };
        const since = toEpoch(beginVal);
        const until = toEpoch(endVal);
        if (Number.isNaN(since) || Number.isNaN(until)) {
            return { since: null, until: null, error: 'Invalid date/time' };
        }
        if (since !== null && until !== null && since > until) {
            return { since: null, until: null, error: '“From” must be before “To”' };
        }
        return { since, until, error: null };
    }

    /**
     * Resolve the task_id to use for a download. Uses the explicitly selected
     * instance, or — when "Current (running)" is selected — the first running
     * task from the loaded instance list. Returns '' if none is resolvable.
     */
    #resolveDownloadTaskId() {
        const sd = this.shadowRoot;
        const selected = sd.getElementById('history-instance-select')?.value || '';
        if (selected) return selected;
        const running = this.#taskInstancesList.find(t => t?.state === 'running');
        return running?.id || '';
    }

    /**
     * Download the full raw log for the selected task instance straight to a
     * file via a native browser download. The browser owns the transfer, so it
     * streams directly to disk with no in-page buffering and works across
     * browsers. Native downloads can't send the bearer header, so we first
     * request a short-lived signed ticket (bearer-authenticated), then point an
     * <a download> at the public download route carrying that ticket.
     */
    async #downloadFullLog() {
        const sd = this.shadowRoot;
        const historyStatus = sd.getElementById('history-status');
        const downloadBtn = sd.getElementById('history-download-btn');

        const effectiveServices = this.#lockedServices ?? this.#selectedServices;
        if (effectiveServices.size !== 1 || this.#tailActive) return;
        const service = Array.from(effectiveServices)[0];
        const taskId = this.#resolveDownloadTaskId();
        if (!taskId) {
            historyStatus.textContent = 'No instance available to download';
            return;
        }

        downloadBtn.disabled = true;
        historyStatus.textContent = 'Preparing download…';
        try {
            const ticketParams = new URLSearchParams({ service, task_id: taskId });
            const resp = await this.fetch(`/api/live/docker/service_logs/download-ticket?${ticketParams.toString()}`);
            const ticket = resp?.ticket;
            if (!ticket) throw new Error('No download ticket returned');

            const dlParams = new URLSearchParams({ dl_ticket: ticket, service, task_id: taskId });
            const url = this.apiUrl(`/api/live/docker/service_logs/download?${dlParams.toString()}`);

            // Trigger a native browser download. The Content-Disposition header
            // from the server supplies the filename; the browser streams to disk.
            const a = document.createElement('a');
            a.href = url;
            a.rel = 'noopener';
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            a.remove();

            historyStatus.textContent = 'Download started';
        } catch (err) {
            Logger.warn('Log download failed:', err);
            const reason = err?.status === 401 ? 'not authorized' : (err?.message || 'download failed');
            historyStatus.textContent = `Error: ${reason}`;
        } finally {
            downloadBtn.disabled = false;
            this.#updateUIState();
        }
    }

    #updateStatus(message) {
        const sd = this.shadowRoot;
        const statusBar = sd.getElementById('status-bar');
        statusBar.textContent = message;
    }

    #exportCsv() {
        const rows = this.#dataView.getFilteredItems ? this.#dataView.getFilteredItems() : this.#dataView.getItems();
        if (!rows || rows.length === 0) {
            this.#updateStatus('Nothing to export — no rows match current filters');
            return;
        }

        const header = [ 'Timestamp', 'Service', 'Severity', 'Message' ];
        const dataRows = rows.map(row => [
            String(row?.timestamp || ''),
            String(row?.service || ''),
            String(row?.severity || ''),
            String(row?.message_plain || row?.message || ''),
        ]);

        const csv = convertJsonToCSV([ header, ...dataRows ]);
        const blob = new Blob([ csv ], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const filename = `live-logs-${ts}.csv`;

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        this.#updateStatus(`Exported ${rows.length} row(s) to ${filename}`);
    }

    #updateLogCount() {
        const sd = this.shadowRoot;
        const count = this.#logsCache.length;
        sd.getElementById('log-count').textContent = `${count} log${count !== 1 ? 's' : ''}`;
    }

    #close() {
        this.#stopTail();
        this.emit({ type: 'hs-close-log-viewer' });
    }
}

customElements.define('hs-log-viewer', HSLogViewer);
